import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Calendar, AlertTriangle, CheckCircle2, FileText, Upload, X, User as UserIcon, FileDown } from "lucide-react";

const FREQ = [["monthly","Monthly"],["quarterly","Quarterly"],["biannual","Biannual"],["annual","Annual"],["custom_days","Custom (days)"]];

export default function PmSchedulePage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [certRow, setCertRow] = useState(null);   // schedule row being edited for cert upload
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", description: "", category: "hvac",
    frequency: "quarterly", custom_days: 90,
    next_due: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    assigned_vendor_id: "", priority: "normal", auto_ticket: true, notes: "",
    inspector_name: "", inspector_email: "", inspector_phone: "",
  });

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/pm-schedule?property_id=${propertyId}`);
      setItems(data.items || []);
      setStats(data.stats || null);
      const { data: v } = await api.get(`/admin/vendors?property_id=${propertyId}`);
      setVendors(v.items || []);
    } catch (e) { toast.error("Failed to load"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [propertyId]);

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      await api.post("/admin/pm-schedule", {
        property_id: propertyId,
        ...form,
        assigned_vendor_id: form.assigned_vendor_id || null,
        custom_days: form.frequency === "custom_days" ? parseInt(form.custom_days) : null,
        inspector_name: form.inspector_name || null,
        inspector_email: form.inspector_email || null,
        inspector_phone: form.inspector_phone || null,
      });
      toast.success("Schedule added");
      setDialog(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markComplete = async (sid) => {
    try {
      const { data } = await api.post(`/admin/pm-schedule/${sid}/complete`);
      toast.success(`Next occurrence: ${data.next_due}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openCertDialog = (row) => {
    setCertRow(row);
    setTimeout(() => fileRef.current?.click(), 300);
  };

  const uploadCert = async (file) => {
    if (!file || !certRow) return;
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) {
      toast.error("PDF only");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8 MB");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post(`/admin/pm-schedule/${certRow.id}/certificate`, {
        filename: file.name,
        data_url: dataUrl,
        inspected_at: new Date().toISOString().slice(0, 10),
      });
      toast.success("Certificate uploaded");
      setCertRow(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const clearCert = async (sid) => {
    if (!window.confirm("Delete this certificate?")) return;
    try {
      await api.delete(`/admin/pm-schedule/${sid}/certificate`);
      toast.success("Certificate removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="pm-schedule-page">
      <PageHeader
        eyebrow="Operations"
        title="Preventive Maintenance"
        subtitle="Recurring HVAC / elevator / roof / life-safety inspections. Cron auto-opens vendor tickets on the due date, and each schedule holds its own inspector contact + latest inspection certificate."
        actions={
          <Button onClick={() => setDialog(true)} className="gap-2" data-testid="new-pm-btn">
            <Plus size={14} /> New schedule
          </Button>
        }
      />

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Schedules</div>
            <div className="text-2xl font-semibold mt-1" data-testid="pm-total">{stats.total}</div>
          </Card>
          <Card className={`p-4 border-white/10 bg-card ${stats.overdue > 0 ? "border-rose-500/40" : ""}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Overdue</div>
            <div className={`text-2xl font-semibold mt-1 ${stats.overdue > 0 ? "text-rose-400" : ""}`} data-testid="pm-overdue">{stats.overdue}</div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Due in 7 days</div>
            <div className="text-2xl font-semibold mt-1 text-amber-500" data-testid="pm-due-7d">{stats.due_within_7d}</div>
          </Card>
        </div>
      )}

      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Inspector</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Next Due</TableHead>
              <TableHead>Last Cert</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm" data-testid="pm-empty">
                No schedules yet. Add HVAC quarterly, elevator monthly, sprinkler bi-annual, roof annual.
              </TableCell></TableRow>
            )}
            {items.map((s) => (
              <TableRow key={s.id} className="border-white/5" data-testid={`pm-row-${s.id}`}>
                <TableCell>
                  <div className="font-medium">{s.name}</div>
                  {s.description && <div className="text-xs text-muted-foreground">{s.description.slice(0, 60)}</div>}
                  {s.assigned_vendor_name && <div className="text-[10px] text-white/40">Vendor: {s.assigned_vendor_name}</div>}
                </TableCell>
                <TableCell><Badge variant="outline">{s.category}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {s.inspector_name ? (
                    <div>
                      <div className="flex items-center gap-1"><UserIcon size={11} />{s.inspector_name}</div>
                      {s.inspector_email && <div className="text-[10px] text-white/40 truncate max-w-[180px]">{s.inspector_email}</div>}
                      {s.inspector_phone && <div className="text-[10px] text-white/40">{s.inspector_phone}</div>}
                    </div>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-sm">{s.frequency}{s.custom_days ? ` · ${s.custom_days}d` : ""}</TableCell>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar size={12} className="text-muted-foreground" />
                    {s.next_due}
                  </div>
                  {s.overdue && <Badge variant="destructive" className="mt-1 text-[10px]"><AlertTriangle size={10} className="mr-1" />Overdue</Badge>}
                </TableCell>
                <TableCell>
                  {s.last_certificate ? (
                    <div className="flex items-center gap-1">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/pm-schedule/${s.id}/certificate`}
                         target="_blank" rel="noreferrer"
                         className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 text-xs"
                         data-testid={`pm-cert-download-${s.id}`}>
                        <FileDown size={11} /> {(s.last_certificate.filename || "cert.pdf").slice(0, 20)}
                      </a>
                      <button onClick={() => clearCert(s.id)}
                              className="text-white/30 hover:text-rose-300 ml-1"
                              data-testid={`pm-cert-clear-${s.id}`}>
                        <X size={10} />
                      </button>
                    </div>
                  ) : <span className="text-white/30 text-xs">—</span>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openCertDialog(s)} className="gap-1 mr-1"
                          data-testid={`pm-cert-upload-${s.id}`}>
                    <Upload size={11} /> Cert
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => markComplete(s.id)} className="gap-1" data-testid={`complete-pm-${s.id}`}>
                    <CheckCircle2 size={12} /> Complete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
             onChange={(e) => e.target.files?.[0] && uploadCert(e.target.files[0])} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pm-dialog">
          <DialogHeader><DialogTitle>New preventive maintenance schedule</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2"><Label>Name</Label>
              <Input placeholder="HVAC Quarterly Inspection" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pm-name" /></div>
            <div className="space-y-2 col-span-2"><Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pm-desc" /></div>
            <div className="space-y-2"><Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pm-cat" /></div>
            <div className="space-y-2"><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="pm-pri"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="pm-freq"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.frequency === "custom_days" && (
              <div className="space-y-2"><Label>Custom days</Label>
                <Input type="number" value={form.custom_days} onChange={(e) => setForm({ ...form, custom_days: e.target.value })} data-testid="pm-days" /></div>
            )}
            <div className="space-y-2"><Label>First occurrence</Label>
              <Input type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} data-testid="pm-next" /></div>
            <div className="space-y-2 col-span-2"><Label>Assigned vendor</Label>
              <Select value={form.assigned_vendor_id || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_vendor_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="pm-vendor"><SelectValue placeholder="Optional — assign later" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 border-t border-white/10 pt-3 mt-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                <UserIcon size={11} /> Inspector contact (technician who runs this inspection)
              </div>
            </div>
            <div className="space-y-2 col-span-2"><Label>Inspector name</Label>
              <Input placeholder="e.g. Marco Silva, HVAC Cert #A12345" value={form.inspector_name} onChange={(e) => setForm({ ...form, inspector_name: e.target.value })} data-testid="pm-inspector-name" /></div>
            <div className="space-y-2"><Label>Inspector email</Label>
              <Input type="email" placeholder="marco@example.com" value={form.inspector_email} onChange={(e) => setForm({ ...form, inspector_email: e.target.value })} data-testid="pm-inspector-email" /></div>
            <div className="space-y-2"><Label>Inspector phone</Label>
              <Input placeholder="+1 (555) 123-4567" value={form.inspector_phone} onChange={(e) => setForm({ ...form, inspector_phone: e.target.value })} data-testid="pm-inspector-phone" /></div>
            <div className="col-span-2 flex items-center gap-2 mt-1">
              <input type="checkbox" checked={form.auto_ticket} onChange={(e) => setForm({ ...form, auto_ticket: e.target.checked })} data-testid="pm-auto" />
              <Label>Auto-open a maintenance ticket on the due date</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={create} data-testid="pm-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!certRow && !uploading} onOpenChange={(o) => !o && setCertRow(null)}>
        <DialogContent className="max-w-md" data-testid="pm-cert-dialog">
          <DialogHeader>
            <DialogTitle>Upload inspection certificate</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            <p><b>{certRow?.name}</b></p>
            <p className="text-xs mt-1">PDF only · max 8 MB</p>
            {certRow?.last_certificate && (
              <p className="text-xs mt-2 text-amber-300">
                A certificate is already on file ({certRow.last_certificate.filename}) — uploading will replace it.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertRow(null)}>Cancel</Button>
            <Button onClick={() => fileRef.current?.click()} data-testid="pm-cert-pick">
              <Upload size={14} className="mr-1" /> Pick PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
